import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Stan powłoki aplikacji: zwinięcie menu (desktop) i jego otwarcie (mobile). Trzymany w kontekście,
 * bo hamburger siedzi w nagłówku, a menu to osobny komponent — 1:1 z Serwisem FTP i mPracownikiem.
 *
 * Provider zakłada Layout (a nie index.js), bo powłoka istnieje wyłącznie na trasach zalogowanych —
 * portal publiczny (`/wniosek/...`) ma własny szkielet i tego stanu nie potrzebuje.
 */
const UIContext = createContext(null);

const COLLAPSE_KEY = "bp-sidebar-collapsed";
/* Próg z kanonicznej siatki 480/640/768/1024/1200 — musi zgadzać się z media query
   w Sidebar.module.css i Header.module.css (drawer + hamburger przy ≤768px). */
const MOBILE_BREAKPOINT = 768;

export const UIProvider = ({ children }) => {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(
        () => localStorage.getItem(COLLAPSE_KEY) === "1"
    );
    const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
            setIsMobile(mobile);
            if (!mobile) {
                setMobileSidebarOpen(false);
            }
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const toggleSidebar = useCallback(() => {
        setSidebarCollapsed((current) => {
            localStorage.setItem(COLLAPSE_KEY, current ? "0" : "1");
            return !current;
        });
    }, []);

    const toggleMobileSidebar = useCallback(() => setMobileSidebarOpen((open) => !open), []);
    const closeMobileSidebar = useCallback(() => setMobileSidebarOpen(false), []);

    const value = useMemo(
        () => ({
            isMobile,
            // Na mobile menu nigdy nie jest „zwinięte" — jest schowane w drawerze.
            isSidebarCollapsed: isMobile ? false : isSidebarCollapsed,
            isMobileSidebarOpen,
            toggleSidebar,
            toggleMobileSidebar,
            closeMobileSidebar,
        }),
        [isMobile, isSidebarCollapsed, isMobileSidebarOpen, toggleSidebar, toggleMobileSidebar, closeMobileSidebar]
    );

    return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = () => {
    const context = useContext(UIContext);
    if (!context) {
        throw new Error("useUI musi być użyty wewnątrz UIProvider.");
    }
    return context;
};
